import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import { Props, Breed, Paginator, Animal } from 'shared/@types';
import { prepareDataForApi } from 'shared/library/api-client';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { BreedCard } from 'shared/components/breed-card';
import { Pagination } from 'shared/components/pagination';
import { Text } from 'shared/components/text';
import { TextInput } from 'shared/components/form/text-input';
import { H1 } from 'shared/components/title';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { BreedsFilterForm } from 'shared/components/breeds-filter-form';
import { BreedsWebFilterControls } from 'shared/components/breeds-web-filter-controls';
import { Button } from 'shared/components/button';
import {
  BreedsFilter,
  BreedsFilterProvider,
  defaultBreedsFilter,
  getBreedsRouteFilter,
  LockedBreedsFilter,
  parseBreedsFilter,
  useBreedsFilterStore,
} from 'shared/state/breeds-filter';
import {
  buildQuery,
  normalizeQuery,
  useDebouncedQueryFilter,
} from 'shared/library/use-debounced-query-filter';

import { PageBuilder } from 'shared/library/page-builder';
import { CardMediaPlaceholder } from 'shared/components/card-media-placeholder';
import { PageContent } from 'shared/components/page-content';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { useRoute } from 'shared/library/routes/route';
import { ProductFeedbackPrompt } from 'shared/components/product-feedback-prompt';
import { useRouter } from 'shared/library/use-router';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';

interface IndexBreedsScreenProps extends Props {
  initialBreeds: Paginator<Breed>;
  initialFilter: BreedsFilter;
  loadingInitial?: boolean;
  title?: string;
  description?: string;
  showAds?: boolean;
  showActiveCounts?: boolean;
  lockedFilter?: LockedBreedsFilter;
}

const IndexBreedsScreen = (props: IndexBreedsScreenProps) => {
  return (
    <AdAnimalProvider animal={props.lockedFilter?.animal ?? props.initialFilter.animal}>
      <BreedsFilterProvider initialState={props.initialFilter}>
        <ScreenContent {...props} />
      </BreedsFilterProvider>
    </AdAnimalProvider>
  );
};

const ScreenContent = ({
  initialBreeds,
  initialFilter,
  loadingInitial = false,
  title,
  description,
  showAds = true,
  showActiveCounts = Platform.OS === 'web',
  lockedFilter,
}: IndexBreedsScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const { asPath, replace } = useRouter();
  const setFilter = useBreedsFilterStore((state) => state.setFilter);
  const filter = useBreedsFilterStore((state) => state.filter);
  const appliedCount = useBreedsFilterStore((state) => state.appliedCount);
  const [breeds, setBreeds] = useState(initialBreeds);
  const lockedAnimal = lockedFilter?.animal;
  const lockedSize = lockedFilter?.size;
  const routeFilter = useMemo(
    () => getBreedsRouteFilter(filter, { animal: lockedAnimal, size: lockedSize }),
    [filter, lockedAnimal, lockedSize],
  );
  const initialRouteFilter = useMemo(
    () => getBreedsRouteFilter(initialFilter, { animal: lockedAnimal, size: lockedSize }),
    [initialFilter, lockedAnimal, lockedSize],
  );
  const buyingGuideBreadcrumb =
    initialFilter.animal === Animal.Dog || initialFilter.animal === Animal.Cat
      ? {
          text: t(
            initialFilter.animal === Animal.Dog
              ? 'web:layout.footer.sections.dogs.buying_a_dog'
              : 'web:layout.footer.sections.cats.buying_a_cat',
          ),
          href: route(initialFilter.animal === Animal.Dog ? 'buying-a-dog' : 'buying-a-cat'),
        }
      : null;

  function resetFilters() {
    setFilter({ ...defaultBreedsFilter, ...lockedFilter });
  }

  useEffect(() => {
    const lockedValues = {
      ...(lockedAnimal !== undefined && filter.animal !== lockedAnimal
        ? { animal: lockedAnimal }
        : {}),
      ...(lockedSize !== undefined && filter.size !== lockedSize ? { size: lockedSize } : {}),
    };

    if (Object.keys(lockedValues).length > 0) {
      setFilter(lockedValues);
    }
  }, [filter.animal, filter.size, lockedAnimal, lockedSize, setFilter]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const [pathWithQuery = ''] = asPath.split('#');
    const [pathname = '', query = ''] = pathWithQuery.split('?');
    const canonicalQuery = buildQuery(initialRouteFilter);

    if (normalizeQuery(query) !== canonicalQuery) {
      void replace(canonicalQuery ? `${pathname}?${canonicalQuery}` : pathname, undefined);
    }
  }, [asPath, initialRouteFilter, replace]);

  useDebouncedQueryFilter(initialRouteFilter, routeFilter, () => {
    setFilter(initialFilter);
    setBreeds(initialBreeds);
  });

  return (
    <PageContent className={'flex-1 !pt-0'} contentClassName={'flex-1'} nativeFlushTop={true}>
      <View className={'flex-1'}>
        <Pagination
          nativeSafeTop={true}
          showAds={showAds}
          adInterval={8}
          mobileAdInterval={4}
          gap={3}
          cols={2}
          mdCols={3}
          lgCols={5}
          mdGap={6}
          loadingInitial={loadingInitial}
          loadingPlaceholderCount={6}
          paginator={breeds}
          onPageChange={(page) => setFilter({ page })}
          renderItem={(breed) => (
            <BreedCard key={breed.id} breed={breed} showActiveCount={showActiveCounts} />
          )}
          GridInsert={
            breeds.current_page === 1
              ? () => (
                  <ProductFeedbackPrompt
                    promptId={'breeds_index'}
                    context={{
                      animal: filter.animal,
                      breed_type: filter.breedType,
                      results_count: breeds.total,
                      applied_filter_count: appliedCount,
                      has_query: Boolean(filter.query),
                    }}
                  />
                )
              : undefined
          }
          gridInsertAfterRows={1}
          renderLoadingPlaceholder={(index) => <LoadingBreedCard key={`loading-breed-${index}`} />}
          ListHeader={() => (
            <>
              {buyingGuideBreadcrumb && (
                <Breadcrumb
                  className={'mb-1.5'}
                  items={[buyingGuideBreadcrumb, { text: title ?? null }]}
                />
              )}
              <H1
                title={title}
                subtitle={description}
                marginBottom={Platform.OS !== 'web'}
                hideTitleWhenNativeHeader={true}
              />
              {Platform.OS === 'web' ? (
                <BreedsWebFilterControls
                  totalBreeds={breeds.total}
                  lockedFilter={lockedFilter}
                  onClear={resetFilters}
                />
              ) : (
                <View className={'flex flex-row items-center gap-3 md:flex-wrap'}>
                  <FullscreenModal
                    headerTitle={t('components:breeds_filter_form.filter_breeds')}
                    headerRight={() => (
                      <TouchableOpacity className={'p-2'} onPress={resetFilters}>
                        <Text className={'text-sm font-bold text-red-700'}>
                          {t('common:clear')}
                        </Text>
                      </TouchableOpacity>
                    )}
                    trigger={({ open }) => (
                      <Button
                        icon={falSlidersSimple}
                        iconPosition={'left'}
                        text={t('components:breeds_filter_form.filter')}
                        color={'white'}
                        dense={true}
                        onPress={open}
                      >
                        <Text
                          className={
                            'bg-brand-cream-dark text-brand-brown-dark ml-1.5 w-5 rounded py-0.5 text-center text-xs font-bold'
                          }
                        >
                          {appliedCount}
                        </Text>
                      </Button>
                    )}
                    footer={({ close }) => (
                      <Button
                        text={t('components:breeds_filter_form.view_breeds')}
                        onPress={close}
                        center={true}
                      >
                        <Text
                          className={'ml-3 rounded bg-black/10 p-1 text-sm font-black text-white'}
                        >
                          {breeds.total}
                        </Text>
                      </Button>
                    )}
                  >
                    <BreedsFilterForm />
                  </FullscreenModal>

                  <TextInput
                    className={'min-w-0 flex-1'}
                    placeholder={t('components:breeds_filter_form.search_by_name')}
                    value={filter.query}
                    dense={true}
                    onChangeText={(val: string) => setFilter({ query: val })}
                  />
                </View>
              )}
            </>
          )}
        />
      </View>
    </PageContent>
  );
};

function LoadingBreedCard() {
  return (
    <View className={'bg-white'}>
      <CardMediaPlaceholder
        className={'aspect-4/3 w-full rounded-t'}
        iconClassName={'text-brand-brown-dark/30 size-9'}
        panelClassName={'bg-brand-cream-dark/15 rounded-3xl p-3'}
      />
      <View className={'border-brand-brown-dark/15 rounded-b border border-t-0 p-3'}>
        <View className={'bg-brand-cream h-7 w-2/3 rounded'} />
        <View className={'bg-brand-cream mt-2 h-4 w-1/3 rounded'} />
      </View>
    </View>
  );
}

IndexBreedsScreen.pageBuilder = new PageBuilder<IndexBreedsScreenProps, BreedsFilter>().request(
  ({ req, params }) => {
    const filter = parseBreedsFilter(params);

    return req<{ breeds: Paginator<Breed> }>(apiRoute('breeds.index'), {
      ...prepareDataForApi(filter, ['characteristics']),
      with_details: true,
    }).then((data) => ({
      initialBreeds: data.breeds,
      initialFilter: filter,
    }));
  },
);

export { IndexBreedsScreen };
export type { IndexBreedsScreenProps };
